import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { defineLiveLocationTask } from './src/media/liveLocationTask';
import { ActivityIndicator as NativeActivityIndicator, View, Text, StyleSheet, BackHandler } from './src/components/rn';
import ActivityIndicator from './src/components/LoadingIndicator';
import { AppState } from 'react-native';
import { Alert } from './src/components/alert';
import AlertHost from './src/components/AlertHost';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { SafeAreaProvider, useSafeAreaInsets } from 'react-native-safe-area-context';
import PhoneScreen from './src/screens/PhoneScreen';
import OtpScreen from './src/screens/OtpScreen';
import TermsScreen from './src/screens/TermsScreen';
import LocationConsentScreen from './src/screens/LocationConsentScreen';
import UniversityScreen from './src/screens/UniversityScreen';
import ProfessionScreen from './src/screens/ProfessionScreen';
import ProfileScreen from './src/screens/ProfileScreen';
import DiscoverScreen from './src/screens/DiscoverScreen';
import UserProfileScreen from './src/screens/UserProfileScreen';
import MessageScreen, { type MessageInitialAction } from './src/screens/MessageScreen';
import { MessageDraftProvider } from './src/screens/MessageDraftContext';
import CallScreen from './src/screens/CallScreen';
import { useCall } from './src/calls/useCall';
import WalletScreen from './src/screens/WalletScreen';
import CalendarScreen from './src/screens/CalendarScreen';
import CalendarConnectionsScreen from './src/screens/CalendarConnectionsScreen';
import ReservationDetailScreen from './src/screens/ReservationDetailScreen';
import InboxScreen from './src/screens/InboxScreen';
import OwnProfileScreen, { type ExceptionListContext } from './src/screens/OwnProfileScreen';
import NotificationSettingsScreen from './src/screens/NotificationSettingsScreen';
import NotificationCenterScreen from './src/screens/NotificationCenterScreen';
import CallHistoryScreen from './src/screens/CallHistoryScreen';
import FloatingDock from './src/components/FloatingDock';
import * as Updates from 'expo-updates';
import { SAFE_SCREENS, useApplyUpdateOnResume } from './src/appVersion/applyUpdateOnResume';
import { ScreenBackButton } from './src/components/ScreenHeader';
import ScreenEdgeBack from './src/components/ChatEdgeBack';
import ForegroundNotificationBanner from './src/components/ForegroundNotificationBanner';
import { storage, decodeToken } from './src/services/storage';
import { api, setUnauthorizedHandler, setNetworkErrorHandler, isFinancialNotification } from './src/services/api';
import {
  ensureDeviceKeys,
  replenishOneTimePreKeys,
  refreshDirectoryEntry,
  isSignalAvailable,
} from './src/e2ee/signalKeys';
import { beginE2eeAccount, endE2eeAccount } from './src/e2ee/account';
import {
  observePushLifecycle,
  shouldShowForegroundMessageBanner,
  type EventNotificationTarget,
  type ForegroundEventNotification,
  type ForegroundMessageNotification,
  retryPendingPushTokenDeletion,
  unregisterStoredPushToken,
} from './src/notifications/pushLifecycle';
import { ThemeProvider, useTheme } from './src/theme/ThemeContext';
import { I18nProvider, useTranslation } from './src/i18n/I18nContext';
import { UnreadProvider, useUnread } from './src/unread/UnreadContext';
import { syncMeetupsToSystemCalendar } from './src/calendar/acceptedMeetupSync';
import InstagramConnectionsScreen from './src/screens/InstagramConnectionsScreen';
import { useSubPageReturn } from './src/navigation/useSubPageReturn';
import { primaryTabBackTarget } from './src/navigation/primaryTabBackTarget';
import { useProfileLinkOpener } from './src/navigation/useProfileLinkOpener';
import {
  useFonts,
  Inter_400Regular,
  Inter_500Medium,
  Inter_600SemiBold,
  Inter_700Bold,
} from '@expo-google-fonts/inter';
import { PlayfairDisplay_400Regular } from '@expo-google-fonts/playfair-display';

// 9.3.3.7 / D-588 — arka plan görevi MODÜL düzeyinde tanımlanıyor. İşletim
// sistemi uygulamayı konum için arka planda başlattığında hiçbir ekran
// kurulmaz; bileşen içinde tanımlansaydı uyandırma boşa giderdi.
defineLiveLocationTask();

// Sessiz uyandırma geldiğinde arka plan ölçümü yeniden başlıyor (9.3.3.7).
// Bu dinleyici de modül düzeyinde: bildirim uygulamayı arka planda
// başlattığında hiçbir ekran kurulmuş olmuyor.
try {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const Notifications = require('expo-notifications');
  Notifications.addNotificationReceivedListener?.((bildirim: any) => {
    if (bildirim?.request?.content?.data?.type !== 'location_wake') return;
    void (async () => {
      const { readLiveShare } = require('./src/media/liveLocationTask');
      if (!await readLiveShare()) return;
      const { startBackgroundLocation } = require('./src/media/backgroundLocation');
      await startBackgroundLocation();
    })();
  });
} catch {
  // Bildirim modülü yoksa uyandırma da yok; paylaşım ön planda çalışmaya devam eder.
}

type Screen = 'loading' | 'phone' | 'otp' | 'terms' | 'locationConsent' | 'university' | 'profession' | 'profile' | 'discover' | 'inbox' | 'ownProfile' | 'settings' | 'userProfile' | 'message' | 'wallet' | 'calendar' | 'reservationDetail' | 'calendarConnections' | 'instagramConnections' | 'notificationSettings' | 'notificationCenter' | 'callHistory';

const appStyles = StyleSheet.create({
  root: { flex: 1 },
  loading: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  offlineBanner: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    backgroundColor: '#e53935',
    paddingVertical: 6,
    alignItems: 'center',
    zIndex: 999,
  },
  offlineText: { color: '#fff', fontSize: 13, fontWeight: '600' },
});

// 7.6 / D-648 — bir alt sayfadan geri dönülebilecek ekranlar. Giriş akışı ve
// yükleme ekranı değil: açılışta gelen bir bildirim cüzdanı açarsa geri tuşu
// oraya değil profile dönmeli.
const RETURNABLE_SCREENS: ReadonlySet<Screen> = new Set<Screen>([
  'discover', 'inbox', 'ownProfile', 'settings', 'calendar', 'callHistory',
  'notificationCenter', 'message', 'userProfile', 'reservationDetail',
]);
function isReturnableScreen(screen: Screen): boolean {
  return RETURNABLE_SCREENS.has(screen);
}

// Onboarding akışında her sayfanın "bir öncekine" haritası (floating geri tuşu).
const backTo: Partial<Record<Screen, Screen>> = {
  otp: 'phone',
  terms: 'otp',
  locationConsent: 'terms',
  university: 'locationConsent',
  profession: 'university',
  profile: 'profession',
};

export default function App() {
  const [fontsLoaded, fontError] = useFonts({
    Inter_400Regular,
    Inter_500Medium,
    Inter_600SemiBold,
    Inter_700Bold,
    PlayfairDisplay_400Regular,
  });

  // Render even if a font fails (fall back to system fonts) so a single
  // asset hiccup can never hang the whole app on the spinner.
  if (!fontsLoaded && !fontError) {
    return (
      <View style={[appStyles.loading, { backgroundColor: '#fff' }]}>
        <NativeActivityIndicator color="#000" accessibilityLabel="Loading" />
      </View>
    );
  }

  return (
    <GestureHandlerRootView style={appStyles.root}>
      <SafeAreaProvider>
        <ThemeProvider>
          <I18nProvider>
            <AppSession />
            {/* The web's alerts and confirmations; renders nothing on iOS and Android. */}
            <AlertHost />
          </I18nProvider>
        </ThemeProvider>
      </SafeAreaProvider>
    </GestureHandlerRootView>
  );
}

// Oturumu (token) tutan ince katman: okunmamış sayacı sağlayıcısı token'a bağlı
// olduğu için, onu tüketen AppInner'ın **üstünde** durmak zorunda.
function AppSession() {
  const [token, setToken] = useState('');
  return (
    <UnreadProvider token={token}>
      <MessageDraftProvider session={token}>
        <AppInner token={token} setToken={setToken} />
      </MessageDraftProvider>
    </UnreadProvider>
  );
}

function AppInner({ token, setToken }: { token: string; setToken: (t: string) => void }) {
  const colors = useTheme();
  const { t, lang } = useTranslation();
  const insets = useSafeAreaInsets();
  // Dock rozeti (9.26.7.2) sayacı sağlayıcıdan okur; burada yalnız sekme
  // değişiminde tazeleme ve sohbet açılışındaki iyimser düşürme için gerekiyor.
  const { refresh: refreshUnread, markConversationRead } = useUnread();
  const loadingStyle = useMemo(
    () => [appStyles.loading, { backgroundColor: colors.white }],
    [colors.white]
  );
  const [screen, setScreen] = useState<Screen>('loading');
  const [phone, setPhone] = useState('');
  const [universityId, setUniversityId] = useState<number | undefined>();
  const [iscedLevel, setIscedLevel] = useState<number | undefined>();
  const [professionId, setProfessionId] = useState<number | undefined>();
  const [myId, setMyId] = useState<number>(0);
  // Sesli arama (9.4): çağrı akışı ekranlardan bağımsız yaşıyor — gelen çağrı
  // hangi ekranda olursak olalım gelebilir, bu yüzden hook kökte duruyor.
  const callState = useCall(token || null);
  const refreshIncomingCall = callState.refreshIncoming;
  // 8.6 / D-703 — inen güncelleme, uygulamaya geri dönülünce ve kimse bir şey
  // kaybetmeyecekse devreye girer; iki kez kapatıp açmak gerekmez.
  const updatePending = Updates.useUpdates().isUpdatePending;
  useApplyUpdateOnResume({ pending: updatePending, safe: callState.phase === 'idle' && SAFE_SCREENS.has(screen) });
  const [callPeer, setCallPeer] = useState<{ name: string; photo?: string | null }>({ name: '' });

  // 9.4.14 / D-596 — cevaplayan tarafta karşı kimliği koru. Arayan yolları
  // (rezervasyon, mesaj ekranı) `callPeer`'i çağrıyı BAŞLATIRKEN kuruyor;
  // cevaplayan hiç kurmuyordu. Gelen çağrı ekranı `incoming.caller_*` kullanır,
  // ama çağrı bitince `incoming` temizlenir ve ekran `callPeer`'e düşerdi —
  // cevaplayanda o boştu, yani "çağrı sona erdi" ekranında ad kaybolur ve avatar
  // "?" olurdu. Gelen çağrı gelince kimliği buraya kopyalıyoruz ki bitişte kalsın.
  const incoming = callState.incoming;
  useEffect(() => {
    if (!incoming) return;
    const ad = [incoming.caller_name, incoming.caller_surname].filter(Boolean).join(' ');
    setCallPeer({ name: ad, photo: incoming.caller_photo });
  }, [incoming]);
  const [selectedUserId, setSelectedUserId] = useState<number>(0);
  const [selectedUserName, setSelectedUserName] = useState('');
  const [selectedUserPhoto, setSelectedUserPhoto] = useState<string | null>(null);
  const [messageFrom, setMessageFrom] = useState<'userProfile' | 'inbox' | 'notificationCenter'>('userProfile');
  const [messageInitialAction, setMessageInitialAction] = useState<MessageInitialAction>(null);
  // Ayrıntıya liste anlık görüntüsü taşınmaz; yalnız kimlik taşınır ve ekran
  // kanonik kaydı kendi yaşam döngüsünde okur.
  const [selectedReservationId, setSelectedReservationId] = useState<number | null>(null);
  const [userProfileFrom, setUserProfileFrom] = useState<'discover' | 'message' | 'callHistory' | 'ownProfile'>('discover');
  const [userProfileHistory, setUserProfileHistory] = useState<number[]>([]);
  const [ownProfileExceptionReturn, setOwnProfileExceptionReturn] = useState<ExceptionListContext | null>(null);
  const [notificationCenterFrom, setNotificationCenterFrom] = useState<'discover' | 'inbox' | 'calendar' | 'callHistory' | 'ownProfile'>('discover');
  const [offline, setOffline] = useState(false);
  const [foregroundNotification, setForegroundNotification] = useState<
    | ({ kind: 'message' } & ForegroundMessageNotification)
    | ({ kind: 'event' } & ForegroundEventNotification)
    | null
  >(null);
  const screenRef = useRef<Screen>(screen);
  const selectedUserIdRef = useRef(selectedUserId);

  const openLinkedUserProfile = useCallback((userId: number) => {
    if (!userId || userId === selectedUserId) return;
    setUserProfileHistory((history) => [...history, selectedUserId]);
    setSelectedUserId(userId);
  }, [selectedUserId]);

  const goBackFromUserProfile = useCallback(() => {
    if (userProfileHistory.length === 0) {
      setScreen(userProfileFrom);
      return;
    }
    const previousUserId = userProfileHistory[userProfileHistory.length - 1];
    setUserProfileHistory((history) => history.slice(0, -1));
    setSelectedUserId(previousUserId);
  }, [userProfileFrom, userProfileHistory]);

  const syncCalendar = useCallback(() => {
    if (!token || !myId) return;
    syncMeetupsToSystemCalendar(token, myId, {
      meetupTitle: (name) => t('calendar.systemEventMeetupTitle', { name }),
      callTitle: (name) => t('calendar.systemEventCallTitle', { name }),
      notes: t('calendar.systemEventNotes'),
    }).catch(() => {});
  }, [myId, t, token]);

  useEffect(() => { screenRef.current = screen; }, [screen]);
  // 7.6 / D-648 — cüzdan, bildirim tercihleri, takvim ve Instagram bağlantıları
  // nereden açıldıysa oraya döner (Ayarlar'dan açılan sayfa Profil'e atlıyordu).
  const subPage = useSubPageReturn<Screen>(setScreen, 'ownProfile', isReturnableScreen);
  // 1.3.5 / D-649 — `nosubject.co/{ad}` linki uygulamada açılınca o profil.
  // Giriş yapılmamışsa link bekler; giriş bitip bir ana ekrana gelince açılır.
  const openLinkedUser = useCallback((id: number) => {
    setSelectedUserId(id);
    setUserProfileHistory([]);
    setUserProfileFrom('discover');
    setScreen('userProfile');
  }, []);
  const openLinkedOwnProfile = useCallback(() => setScreen('ownProfile'), []);
  const linkedProfileMissing = useCallback(() => Alert.alert(t('userProfile.failed')), [t]);
  useProfileLinkOpener({
    token,
    myId,
    ready: isReturnableScreen(screen),
    openUser: openLinkedUser,
    openOwnProfile: openLinkedOwnProfile,
    onNotFound: linkedProfileMissing,
  });
  useEffect(() => { selectedUserIdRef.current = selectedUserId; }, [selectedUserId]);

  useEffect(() => {
    syncCalendar();
    const subscription = AppState.addEventListener('change', (state) => {
      if (state === 'active') syncCalendar();
    });
    return () => subscription.remove();
  }, [syncCalendar]);

  const dismissForegroundNotification = useCallback(() => {
    setForegroundNotification(null);
  }, []);

  const openMessageNotification = useCallback((senderId: number) => {
    setForegroundNotification(null);
    markConversationRead(senderId);
    setSelectedUserId(senderId);
    setMessageFrom('inbox');
    setMessageInitialAction(null);
    api.getUser(senderId, undefined, token)
      .then((user) => {
        setSelectedUserName([user.name, user.surname].filter(Boolean).join(' '));
        setSelectedUserPhoto(user.photo_url);
        setScreen('message');
      })
      .catch(() => setScreen('inbox'));
  }, [markConversationRead, token]);

  const openNotificationCenter = useCallback((from: 'discover' | 'inbox' | 'calendar' | 'callHistory' | 'ownProfile') => {
    setNotificationCenterFrom(from);
    setScreen('notificationCenter');
  }, []);

  const openEventNotification = useCallback((target: EventNotificationTarget) => {
    setForegroundNotification(null);
    api.markNotificationRead(token, target.eventId)
      .then(() => refreshUnread())
      .catch(() => refreshUnread());
    if (target.destination === 'wallet') {
      // Bildirim hangi ekrandayken açıldıysa cüzdandan geri oraya.
      subPage.open('wallet', screenRef.current);
      return;
    }
    if (target.destination === 'reservation') {
      setMessageInitialAction(null);
      setSelectedReservationId(target.entityId);
      setScreen('reservationDetail');
      return;
    }
    setSelectedUserId(target.actorId);
    setMessageFrom('notificationCenter');
    setMessageInitialAction(null);
    api.getUser(target.actorId, undefined, token)
      .then((user) => {
        setSelectedUserName([user.name, user.surname].filter(Boolean).join(' '));
        setSelectedUserPhoto(user.photo_url);
        setScreen('message');
      })
      .catch(() => {
        setNotificationCenterFrom('inbox');
        setScreen('notificationCenter');
      });
  }, [refreshUnread, token, subPage.open]);

  useEffect(() => {
    // Önceki çıkış ağ kesintisine denk geldiyse tek-kayıtlık silme anahtarıyla
    // arka planda tamamla; oturum yüklenmesini veya ilk ekranı bekletme.
    retryPendingPushTokenDeletion().catch(() => {});
    storage.getToken().then((saved) => {
      if (saved) {
        // D-711 — anahtar kapsamı, ekranlar şifreleme işine başlamadan kurulur.
        void beginE2eeAccount(saved, decodeToken(saved)?.userId ?? 0);
        setToken(saved);
        setMyId(decodeToken(saved)?.userId ?? 0);
        setScreen('discover');
      } else {
        setScreen('phone');
      }
    });

    setUnauthorizedHandler(async () => {
      await storage.clearToken();
      endE2eeAccount();
      setToken('');
      setMyId(0);
      setScreen('phone');
    });

    setNetworkErrorHandler(showOffline);
  }, [setToken]);

  // 9.26 / D-640 — uygulamanın dilini sunucuya söyle. Sunucu bir üyenin dilini
  // yalnız telefonun bildirim kaydından biliyordu; bildirim izni vermemiş
  // üyeninki bilinmiyor ve duyuru İngilizce gidiyordu (34 kişiden 32'si).
  // Açılışta ve dil değiştiğinde bir kez; hata kullanıcıya gösterilmiyor,
  // sonraki açılış yeniden dener.
  useEffect(() => {
    if (!token || !lang) return;
    api.setMyLanguage(token, lang).catch(() => {});
  }, [token, lang]);

  // 9.34 / D-613 — cihazın uçtan uca şifreleme anahtarlarını dizine yayımla.
  // Bir kez kaydeder (ikinci çağrı hiçbir şey yapmaz), sonra tek-kullanımlık
  // prekey deposu azaldıysa tazeler.
  //
  // Başarısızlık kullanıcıya gösterilmiyor ve akışı kesmiyor: anahtar
  // yayımlanamazsa şifreleme açılmaz, mesajlaşma bugünkü gibi düz metinle
  // sürer. Kullanıcının istemediği bir özellik için hata ekranı çıkarmak yanlış
  // olurdu — ama sessiz de kalmıyor, günlüğe düşüyor.
  useEffect(() => {
    if (!token) return;
    let iptal = false;
    (async () => {
      // 9.34 / D-633 — depo yazamıyorsa (gizli sekme gibi) hiç başlama.
      // Eskiden burası denenir, atar, yakalanır ve kimse fark etmezdi.
      if (!isSignalAvailable()) {
        console.warn('[e2ee] Anahtar deposu bu ortamda yazılamıyor; şifreleme açılmadı (D-633)');
        return;
      }
      try {
        await ensureDeviceKeys(token);
        if (iptal) return;
        // 9.34 / D-629 — dizindeki imzalı prekey, cihazın elindeki özelden
        // türetilen olsun. Özeli kaybolmuş bir cihaz aksi hâlde kendisine
        // kurulan HER yeni oturumu sessizce kaybediyordu.
        const imzali = await refreshDirectoryEntry(token);
        if (imzali === 'yenilendi') {
          console.warn('[e2ee] İmzalı prekey yenilendi — dizindekinin özeli yoktu (D-629)');
        }
        if (iptal) return;
        await replenishOneTimePreKeys(token);
      } catch (error) {
        console.warn('[e2ee] Cihaz anahtarları yayımlanamadı (9.34):', error);
      }
    })();
    return () => { iptal = true; };
  }, [token]);

  // Bildirim izni burada istenmez. Daha önce izin verilmişse token sessizce
  // eşlenir; yeni mesaj öndeyken sayaç tazelenir, bildirime dokunulunca sohbet
  // açılır. Native modül eklenmemiş eski build'lerde servis sessizce geri düşer.
  useEffect(() => {
    if (!token) return;
    return observePushLifecycle({
      authToken: token,
      language: lang,
      onMessageReceived: (notification) => {
        refreshUnread();
        // D-127: mesaj zaten görünür olan sohbete geldiyse ikinci bir yüzey üretme.
        if (!shouldShowForegroundMessageBanner(
          screenRef.current,
          selectedUserIdRef.current,
          notification.senderId
        )) return;
        setForegroundNotification({ kind: 'message', ...notification });
      },
      onNotificationEventReceived: (notification) => {
        refreshUnread();
        // Gelen aramada CallScreen'i kökteki yoklama açar. Aynı anda ikinci
        // bir uygulama-içi banner göstermek yerine push sesli uyandırır ve
        // kesin çağrı kaydını seyrek emniyet yoklamasını beklemeden okutur.
        if (notification.eventType === 'call_incoming') {
          refreshIncomingCall();
          return;
        }
        if (notification.eventType === 'meetup_accepted'
          || notification.eventType === 'call_reservation_accepted'
          || notification.eventType === 'meetup_cancelled'
          || notification.eventType === 'call_reservation_cancelled') {
          syncCalendar();
        }
        if (notification.destination === 'conversation'
          && !shouldShowForegroundMessageBanner(
            screenRef.current,
            selectedUserIdRef.current,
            notification.actorId
          )) return;
        setForegroundNotification({ kind: 'event', ...notification });
      },
      onMessageOpened: openMessageNotification,
      onNotificationEventOpened: openEventNotification,
      onNotificationOpenFallback: () => setScreen('inbox'),
    });
  }, [token, lang, refreshUnread, openMessageNotification, openEventNotification, syncCalendar,
    refreshIncomingCall]);

  // Android donanım geri tuşu: ekran-bazlı geri gezinme. Bir önceki ekran varsa
  // ona dön (true = ele alındı); yoksa (discover/phone) sistemin varsayılanına
  // bırak (false = uygulamadan çık). Açık bir Modal'ın kendi onRequestClose'u önce çalışır.
  useEffect(() => {
    const onBack = () => {
      const prev = backTo[screen];
      if (prev) { setScreen(prev); return true; }
      switch (screen) {
        case 'wallet':
        case 'notificationSettings':
        case 'calendarConnections':
        case 'instagramConnections': subPage.back(); return true;
        case 'settings': setScreen('ownProfile'); return true;
        case 'notificationCenter': setScreen(notificationCenterFrom); return true;
        case 'userProfile': goBackFromUserProfile(); return true;
        case 'message': setScreen(messageFrom); return true;
        case 'reservationDetail': setScreen('calendar'); return true;
        case 'inbox':
        case 'calendar':
        case 'callHistory':
        case 'ownProfile': setScreen('discover'); return true;
        default: return false;
      }
    };
    const sub = BackHandler.addEventListener('hardwareBackPress', onBack);
    return () => sub.remove();
  }, [screen, messageFrom, notificationCenterFrom, goBackFromUserProfile, subPage.back]);

  function showOffline() {
    setOffline(true);
    setTimeout(() => setOffline(false), 3000);
  }

  async function handleVerify(jwt: string, onboarded: boolean) {
    await storage.setToken(jwt);
    void beginE2eeAccount(jwt, decodeToken(jwt)?.userId ?? 0);
    setToken(jwt);
    setMyId(decodeToken(jwt)?.userId ?? 0);
    // Profili tamamlanmış (dönen) kullanıcı doğrudan uygulamaya; yeni kullanıcı
    // kayıt akışına. Böylece her girişte aynı bilgiler tekrar sorulmaz.
    setScreen(onboarded ? 'discover' : 'terms');
  }

  async function handleLogout() {
    await unregisterStoredPushToken(token);
    await storage.clearToken();
    endE2eeAccount();
    setToken('');
    setMyId(0);
    setScreen('phone');
  }

  if (screen === 'loading') {
    return (
      <View style={loadingStyle}>
        <ActivityIndicator color={colors.black} />
      </View>
    );
  }

  const dockActive = screen === 'discover' ? 'discover'
    : screen === 'inbox' ? 'inbox'
    : screen === 'callHistory' ? 'calls'
    : screen === 'calendar' ? 'calendar'
    : screen === 'ownProfile' || screen === 'settings' ? 'profile'
    : null;

  // Match visible Back destinations and Android's existing primary-tab fallback.
  // Chat, wallet and notification settings own their internal Back state.
  const pageBack: Partial<Record<Screen, () => void>> = {
    calendarConnections: subPage.back,
    instagramConnections: subPage.back,
    settings: () => setScreen('ownProfile'),
    notificationCenter: () => setScreen(notificationCenterFrom),
    userProfile: goBackFromUserProfile,
    reservationDetail: () => setScreen('calendar'),
  };
  const previous = backTo[screen];
  const primaryBack = primaryTabBackTarget(screen);
  const onEdgeBack = previous ? () => setScreen(previous)
    : pageBack[screen] ?? (primaryBack ? () => setScreen(primaryBack) : undefined);

  return (
    <ScreenEdgeBack enabled={!!onEdgeBack && callState.phase === 'idle'}
      onBack={() => onEdgeBack?.()}>
      {offline && (
        <View style={[appStyles.offlineBanner, { paddingTop: insets.top + 6 }]}>
          <Text style={appStyles.offlineText}>{t('message.noConnection')}</Text>
        </View>
      )}
      {foregroundNotification && (
        <ForegroundNotificationBanner
          notification={foregroundNotification}
          fallbackBody={t('notifications.newMessage')}
          onPress={() => foregroundNotification.kind === 'message'
            ? openMessageNotification(foregroundNotification.senderId)
            : openEventNotification(foregroundNotification)}
          onDismiss={dismissForegroundNotification}
        />
      )}
      {backTo[screen] && (
        <ScreenBackButton onPress={() => setScreen(backTo[screen]!)} />
      )}
      {screen === 'phone' && (
        <PhoneScreen
          onVerify={handleVerify}
          onContinue={(phoneNumber) => {
            setPhone(phoneNumber);
            setScreen('otp');
          }}
        />
      )}
      {screen === 'otp' && (
        <OtpScreen
          phone={phone}
          onVerify={handleVerify}
        />
      )}
      {screen === 'terms' && (
        <TermsScreen
          token={token}
          onAccept={() => setScreen('locationConsent')}
        />
      )}
      {screen === 'locationConsent' && (
        <LocationConsentScreen onContinue={() => setScreen('university')} />
      )}
      {screen === 'university' && (
        <UniversityScreen
          onSelect={(uni, level) => {
            setUniversityId(uni.id);
            setIscedLevel(level);
            setScreen('profession');
          }}
          onSkip={() => setScreen('profession')}
        />
      )}
      {screen === 'profession' && (
        <ProfessionScreen
          onSelect={(prof) => {
            setProfessionId(prof.id);
            setScreen('profile');
          }}
          onSkip={() => setScreen('profile')}
        />
      )}
      {screen === 'profile' && (
        <ProfileScreen
          token={token}
          universityId={universityId}
          iscedLevel={iscedLevel}
          professionId={professionId}
          onContinue={() => setScreen('discover')}
        />
      )}
      {screen === 'discover' && (
        <DiscoverScreen
          token={token}
          onSelectUser={(id) => {
            setSelectedUserId(id);
            setUserProfileHistory([]);
            setUserProfileFrom('discover');
            setScreen('userProfile');
          }}
          onWallet={() => subPage.open('wallet', 'discover')}
          onNotificationCenter={() => openNotificationCenter('discover')}
        />
      )}
      {screen === 'inbox' && (
        <InboxScreen
          token={token}
          myId={myId}
          onSelectConversation={(userId, userName, userPhoto) => {
            // Sohbeti açmak sunucuda mesajları okundu yapıyor; rozet cevabı
            // beklemeden düşsün (9.26.7.4.1 — iyimser güncelleme).
            markConversationRead(userId);
            setSelectedUserId(userId);
            setSelectedUserName(userName);
            setSelectedUserPhoto(userPhoto);
            setMessageFrom('inbox');
            setMessageInitialAction(null);
            setScreen('message');
          }}
          onNotificationCenter={() => openNotificationCenter('inbox')}
        />
      )}
      {screen === 'ownProfile' && (
        <OwnProfileScreen
          token={token}
          myId={myId}
          onWallet={() => subPage.open('wallet', 'ownProfile')}
          onNotifications={() => subPage.open('notificationSettings', 'ownProfile')}
          onCalendarConnections={() => subPage.open('calendarConnections', 'ownProfile')}
          onInstagramConnections={() => subPage.open('instagramConnections', 'ownProfile')}
          onNotificationCenter={() => openNotificationCenter('ownProfile')}
          exceptionReturnContext={ownProfileExceptionReturn}
          onExceptionReturnHandled={() => setOwnProfileExceptionReturn(null)}
          onOpenUserProfile={(id, context) => {
            setOwnProfileExceptionReturn(context);
            setSelectedUserId(id);
            setUserProfileHistory([]);
            setUserProfileFrom('ownProfile');
            setScreen('userProfile');
          }}
          onLogout={handleLogout}
          onSettings={() => setScreen('settings')}
        />
      )}
      {/* 7.6 — ayarlar aynı bileşenin ikinci görünümü. Durum, para ve izin
          mantığı tek yerde kaldığı için taşıma sırasında bozulma riski yok. */}
      {screen === 'settings' && (
        <OwnProfileScreen
          variant="settings"
          token={token}
          myId={myId}
          onBack={() => setScreen('ownProfile')}
          onWallet={() => subPage.open('wallet', 'settings')}
          onNotifications={() => subPage.open('notificationSettings', 'settings')}
          onCalendarConnections={() => subPage.open('calendarConnections', 'settings')}
          onInstagramConnections={() => subPage.open('instagramConnections', 'settings')}
          onNotificationCenter={() => openNotificationCenter('ownProfile')}
          onOpenUserProfile={(id, context) => {
            setOwnProfileExceptionReturn(context);
            setSelectedUserId(id);
            setUserProfileHistory([]);
            setUserProfileFrom('ownProfile');
            setScreen('userProfile');
          }}
          onLogout={handleLogout}
        />
      )}
      {screen === 'notificationSettings' && (
        <NotificationSettingsScreen
          token={token}
          onBack={subPage.back}
        />
      )}
      {screen === 'callHistory' && (
        <CallHistoryScreen
          token={token}
          onNotificationCenter={() => openNotificationCenter('callHistory')}
          onOpenUser={(id) => {
            setSelectedUserId(id);
            setUserProfileHistory([]);
            setUserProfileFrom('callHistory');
            setScreen('userProfile');
          }}
        />
      )}
      {screen === 'wallet' && (
        <WalletScreen
          token={token}
          myId={myId}
          onBack={subPage.back}
        />
      )}
      {screen === 'calendar' && (
        <CalendarScreen
          token={token}
          myId={myId}
          onNotificationCenter={() => openNotificationCenter('calendar')}
          onOpenReservation={(reservationId) => {
            setSelectedReservationId(reservationId);
            setScreen('reservationDetail');
          }}
        />
      )}
      {screen === 'reservationDetail' && selectedReservationId !== null && (
        <ReservationDetailScreen
          token={token}
          myId={myId}
          meetupId={selectedReservationId}
          onBack={() => setScreen('calendar')}
          onStartCall={(reservation) => {
            const otherId = reservation.direction === 'sent'
              ? reservation.recipient_id
              : reservation.requester_id;
            setSelectedUserId(otherId);
            setCallPeer({ name: reservation.other_name, photo: reservation.other_photo });
            callState.start(otherId, reservation.id).catch(() => {});
          }}
        />
      )}
      {screen === 'calendarConnections' && (
        <CalendarConnectionsScreen
          token={token}
          userId={myId}
          onBack={subPage.back}
        />
      )}
      {screen === 'instagramConnections' && (
        <InstagramConnectionsScreen
          token={token}
          onBack={subPage.back}
        />
      )}
      {screen === 'notificationCenter' && (
        <NotificationCenterScreen
          token={token}
          onBack={() => setScreen(notificationCenterFrom)}
          onOpen={(event) => {
            refreshUnread();
            // Para olayı Cüzdan'ı açar. Ölçüt varlık türü DEĞİL olayın kendisi:
            // kazancın varlığı cevap mesajıdır ve tür ölçütüyle gidilseydi
            // kazanca dokunmak takvimi açardı (9.26.3.1.3).
            if (isFinancialNotification(event.type) || event.entity_type === 'transaction') {
              subPage.open('wallet', 'notificationCenter');
              return;
            }
            if (
              event.entity_type === 'meetup'
              || event.type.startsWith('meetup_')
              || event.type.startsWith('call_reservation_')
            ) {
              setMessageInitialAction(null);
              setSelectedReservationId(event.entity_id);
              setScreen('reservationDetail');
              return;
            }
            if (!event.actor_id) {
              setScreen('calendar');
              return;
            }
            setSelectedUserId(event.actor_id);
            setMessageFrom('notificationCenter');
            setMessageInitialAction(null);
            api.getUser(event.actor_id, undefined, token)
              .then((user) => {
                setSelectedUserName([user.name, user.surname].filter(Boolean).join(' '));
                setSelectedUserPhoto(user.photo_url);
                setScreen('message');
              })
              .catch(() => setScreen('calendar'));
          }}
        />
      )}
      {screen === 'userProfile' && (
        <UserProfileScreen
          key={selectedUserId}
          userId={selectedUserId}
          myId={myId}
          token={token}
          onBack={goBackFromUserProfile}
          onOpenProfile={openLinkedUserProfile}
          // D-305 — engelledikten sonra profilden ATMIYORUZ. Eylemin sonucu
          // eylemin yapıldığı yerde görünmeli: buton "Engeli kaldır"a döner ve
          // yanlışlıkla basan tek dokunuşla geri alabilir. Keşif listesi
          // tazeliğini kaybetmez; ekran koşullu render edildiği için oraya
          // dönüldüğünde bileşen yeniden kurulup veriyi yeniden çeker.
          onBlocked={() => {}}
          onStarredMessages={(name, photo) => {
            setSelectedUserName(name);
            setSelectedUserPhoto(photo);
            setMessageFrom('userProfile');
            setMessageInitialAction('stars');
            setScreen('message');
          }}
          onMessage={(name, photo) => {
            markConversationRead(selectedUserId);
            setSelectedUserName(name);
            setSelectedUserPhoto(photo);
            setMessageFrom('userProfile');
            setMessageInitialAction(null);
            setScreen('message');
          }}
          onProposeMeetup={(name, photo) => {
            markConversationRead(selectedUserId);
            setSelectedUserName(name);
            setSelectedUserPhoto(photo);
            setMessageFrom('userProfile');
            setMessageInitialAction('meetup');
            setScreen('message');
          }}
          onPlanCall={(name, photo) => {
            markConversationRead(selectedUserId);
            setSelectedUserName(name);
            setSelectedUserPhoto(photo);
            setMessageFrom('userProfile');
            setMessageInitialAction('callReservation');
            setScreen('message');
          }}
        />
      )}
      {screen === 'message' && (
        <MessageScreen
          key={`${myId}:${selectedUserId}`}
          token={token}
          myId={myId}
          otherId={selectedUserId}
          otherName={selectedUserName}
          otherPhoto={selectedUserPhoto}
          paused={callState.phase !== 'idle'}
          initialAction={messageInitialAction}
          onInitialActionHandled={() => setMessageInitialAction(null)}
          onForwardDraft={(peer, text) => {
            setSelectedUserId(peer.other_id);
            setSelectedUserName(peer.other_name);
            setSelectedUserPhoto(peer.other_photo);
            setMessageInitialAction({ type: 'forwardDraft', text });
            setMessageFrom('inbox');
          }}
          onForwardMedia={(peer,source)=>{
            setSelectedUserId(peer.other_id);setSelectedUserName(peer.other_name);setSelectedUserPhoto(peer.other_photo);
            setMessageInitialAction({type:'forwardMedia',source});setMessageFrom('inbox');
          }}
          onBack={() => setScreen(messageFrom)}
          onViewProfile={() => {
            setUserProfileHistory([]);
            setUserProfileFrom('message');
            setScreen('userProfile');
          }}
          onStartCall={(reservationId, priceStamp, video) => {
            setCallPeer({ name: selectedUserName, photo: selectedUserPhoto });
            void callState.start(selectedUserId, reservationId, priceStamp, video === true);
          }}
        />
      )}

      {/* Çağrı ekranı her şeyin üstünde: konuşma sürerken alttaki ekranın
          durumu korunur, çağrı bitince kullanıcı bıraktığı yere döner. */}
      {callState.phase !== 'idle' && (
        <View style={StyleSheet.absoluteFill} accessibilityViewIsModal>
          <CallScreen
            {...callState}
            peerName={callState.incoming
              ? [callState.incoming.caller_name, callState.incoming.caller_surname].filter(Boolean).join(' ')
              : callPeer.name}
            peerPhoto={callState.incoming ? callState.incoming.caller_photo : callPeer.photo}
            onAccept={() => void callState.accept()}
            onDecline={() => void callState.decline()}
            onHangUp={() => void callState.hangUp()}
            onToggleMute={callState.toggleMute}
            onToggleCamera={callState.toggleCamera}
            onSwitchCamera={callState.switchCamera}
            onSelectAudioRoute={callState.selectAudioRoute}
            onClose={callState.reset}
          />
        </View>
      )}

      {/* Çağrı tam ekran bir moddur: dock çağrı ekranından sonra render edildiği
          için üstünde kalıyor ve Kabul/Reddet butonlarını örtüyordu (9.4.11.1). */}
      {dockActive && callState.phase === 'idle' && (
        <FloatingDock
          active={dockActive}
          onPress={(tab) => {
            // Sekme değişimi bir tazeleme tetikleyicisi (9.26.7.4.1): uygulama
            // genelinde yoklama seyrek olduğu için kullanıcının her dokunuşu
            // sayacı da güncel tutuyor.
            refreshUnread();
            if (tab === 'discover') setScreen('discover');
            else if (tab === 'inbox') setScreen('inbox');
            else if (tab === 'calls') setScreen('callHistory');
            else if (tab === 'calendar') setScreen('calendar');
            else if (tab === 'profile') setScreen('ownProfile');
          }}
        />
      )}
    </ScreenEdgeBack>
  );
}
